import React, { useState, useEffect, useMemo } from "react";
import { Search, MessageCircle, Gauge, Fuel, Cog, Tag, X, Filter, FileText } from "lucide-react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { Dialog, DialogContent } from "./ui/dialog";
import { usePreventivo } from "./PreventivoContext";

const PER_PAGE = 12;
const fmt = new Intl.NumberFormat("it-IT");
const price = (n) => (n != null ? `\u20ac ${fmt.format(n)}` : "Su richiesta");

const Card = ({ m, onOpen, onQuote }) => (
  <div className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all overflow-hidden flex flex-col">
    <button onClick={() => onOpen(m)} className="relative h-44 bg-slate-100 overflow-hidden text-left">
      <img src={m.foto} alt={m.titolo} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={(e) => { e.currentTarget.style.display = "none"; }} />
      {m.tag && (
        <span className={`absolute top-3 left-3 text-[11px] font-semibold px-2.5 py-1 rounded-full text-white ${m.tag === "Performance" ? "bg-[#e8464a]" : "bg-[#0f2846]"}`}>{m.tag}</span>
      )}
      <span className="absolute top-3 right-3 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/90 text-[#0f2846]">{m.marca}</span>
    </button>
    <div className="p-5 flex flex-col flex-1">
      <button onClick={() => onOpen(m)} className="text-left">
        <h3 className="font-display font-bold text-[#0f2846] text-[15px] leading-snug line-clamp-2 hover:text-[#e8464a] transition-colors">{m.titolo}</h3>
      </button>
      {m.codice && <p className="text-slate-400 text-xs mt-1 font-mono">Cod. {m.codice}</p>}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-slate-500 text-xs">
        {m.carburante && m.carburante !== "N/D" && <span className="flex items-center gap-1"><Fuel size={13} /> {m.carburante}</span>}
        {m.cv && <span className="flex items-center gap-1"><Gauge size={13} /> {m.cv} CV</span>}
        {m.km != null && <span className="flex items-center gap-1"><Cog size={13} /> {fmt.format(m.km)} km</span>}
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between gap-2">
        <div>
          {m.prezzoOrig && m.prezzo && m.prezzoOrig > m.prezzo && (
            <div className="text-slate-400 text-xs line-through">{price(m.prezzoOrig)}</div>
          )}
          <div className="font-display font-bold text-[#0f2846] text-xl">{price(m.prezzo)}</div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onQuote(m); }}
          className="shrink-0 inline-flex items-center gap-1.5 bg-[#e8464a] hover:bg-[#d43a3e] text-white text-xs font-semibold px-3.5 py-2.5 rounded-lg transition-colors">
          <FileText size={15} /> Preventivo
        </button>
      </div>
    </div>
  </div>
);

const Catalog = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [marca, setMarca] = useState("all");
  const [carb, setCarb] = useState("all");
  const [tipo, setTipo] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const { openPreventivo } = usePreventivo();

  const openQuote = (m) => { setSelected(null); openPreventivo(m); };

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL || ""}/catalogo.json`)
      .then((r) => r.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const marche = useMemo(() => [...new Set(data.map((x) => x.marca))].filter(Boolean).sort(), [data]);
  const carburanti = useMemo(() => [...new Set(data.map((x) => x.carburante))].filter((c) => c && c !== "N/D").sort(), [data]);
  const tipologie = useMemo(() => [...new Set(data.map((x) => x.tipologia))].filter(Boolean).sort(), [data]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return data.filter((m) => {
      if (marca !== "all" && m.marca !== marca) return false;
      if (carb !== "all" && m.carburante !== carb) return false;
      if (tipo !== "all" && m.tipologia !== tipo) return false;
      if (t && !(`${m.titolo} ${m.codice} ${m.marca} ${m.compat}`.toLowerCase().includes(t))) return false;
      return true;
    });
  }, [data, q, marca, carb, tipo]);

  useEffect(() => { setPage(1); }, [q, marca, carb, tipo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const resetFilters = () => { setQ(""); setMarca("all"); setCarb("all"); setTipo("all"); };
  const hasFilters = q || marca !== "all" || carb !== "all" || tipo !== "all";

  const pageNums = useMemo(() => {
    const arr = [];
    const s = Math.max(1, page - 2), e = Math.min(totalPages, page + 2);
    for (let i = s; i <= e; i++) arr.push(i);
    return arr;
  }, [page, totalPages]);

  return (
    <section id="catalogo" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display text-[#0f2846] text-3xl md:text-4xl font-bold">Catalogo motori</h2>
          <div className="w-24 h-1 bg-[#e8464a] mx-auto mt-4 rounded-full" />
          <p className="text-slate-500 mt-5">
            Oltre <b className="text-[#0f2846]">{data.length ? fmt.format(data.length) : "1.800"} motori</b> disponibili, testati e verificati. Cerca per marca, modello o codice motore e richiedi il tuo su WhatsApp.
          </p>
        </div>

        {/* Filters */}
        <div className="mt-10 bg-[#f4f6f9] border border-slate-100 rounded-2xl p-4 md:p-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5 relative">
              <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca marca, modello o codice motore..."
                className="w-full h-11 rounded-lg border border-slate-200 bg-white pl-11 pr-4 text-sm outline-none focus:border-[#0f2846] focus:ring-2 focus:ring-[#0f2846]/10 transition" />
            </div>
            <div className="md:col-span-2">
              <Select value={marca} onValueChange={setMarca}>
                <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Marca" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">Tutte le marche</SelectItem>
                  {marche.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Select value={carb} onValueChange={setCarb}>
                <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Carburante" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Carburante</SelectItem>
                  {carburanti.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger className="h-11 bg-white"><SelectValue placeholder="Tipologia" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le tipologie</SelectItem>
                  {tipologie.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-slate-500 flex items-center gap-1.5"><Filter size={15} /> {fmt.format(filtered.length)} risultati</span>
            {hasFilters && (
              <button onClick={resetFilters} className="flex items-center gap-1 text-[#e8464a] font-medium hover:underline"><X size={15} /> Azzera filtri</button>
            )}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {[...Array(8)].map((_, i) => <div key={i} className="h-80 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : pageItems.length === 0 ? (
          <div className="text-center py-20 text-slate-400">Nessun motore trovato con questi filtri.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            {pageItems.map((m) => <Card key={m.id} m={m} onOpen={setSelected} onQuote={openQuote} />)}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-12 flex-wrap">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 h-10 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:border-[#0f2846] transition-colors">Prec.</button>
            {pageNums[0] > 1 && <span className="px-2 text-slate-400">...</span>}
            {pageNums.map((n) => (
              <button key={n} onClick={() => { setPage(n); document.getElementById("catalogo").scrollIntoView({ behavior: "smooth" }); }}
                className={`w-10 h-10 rounded-lg text-sm font-semibold transition-colors ${n === page ? "bg-[#0f2846] text-white" : "border border-slate-200 text-slate-600 hover:border-[#0f2846]"}`}>{n}</button>
            ))}
            {pageNums[pageNums.length - 1] < totalPages && <span className="px-2 text-slate-400">...</span>}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 h-10 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 hover:border-[#0f2846] transition-colors">Succ.</button>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0">
          {selected && (
            <div>
              <div className="h-56 bg-slate-100 overflow-hidden">
                <img src={selected.foto} alt={selected.titolo} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#0f2846] text-white">{selected.marca}</span>
                  {selected.tag && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1"><Tag size={12} /> {selected.tag}</span>}
                  <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">{selected.tipologia}</span>
                </div>
                <h3 className="font-display font-bold text-[#0f2846] text-xl mt-3">{selected.titolo}</h3>
                {selected.codice && <p className="text-slate-400 text-sm mt-1 font-mono">Codice motore: {selected.codice}</p>}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {[["Carburante", selected.carburante !== "N/D" ? selected.carburante : "-"], ["Potenza", selected.cv ? `${selected.cv} CV` : "-"], ["Km", selected.km != null ? fmt.format(selected.km) : "-"]].map(([k, v]) => (
                    <div key={k} className="bg-[#f4f6f9] rounded-lg p-3 text-center">
                      <div className="text-slate-400 text-xs">{k}</div>
                      <div className="font-semibold text-[#0f2846] text-sm mt-0.5">{v}</div>
                    </div>
                  ))}
                </div>
                {selected.compat && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Compatibilità</div>
                    <p className="text-slate-500 text-sm mt-1 leading-relaxed">{selected.compat}...</p>
                  </div>
                )}
                <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100">
                  <div>
                    {selected.prezzoOrig && selected.prezzo && selected.prezzoOrig > selected.prezzo && (
                      <span className="text-slate-400 text-sm line-through mr-2">{price(selected.prezzoOrig)}</span>
                    )}
                    <span className="font-display font-bold text-[#0f2846] text-2xl">{price(selected.prezzo)}</span>
                  </div>
                  <button onClick={() => openQuote(selected)}
                    className="inline-flex items-center gap-2 bg-[#e8464a] hover:bg-[#d43a3e] text-white font-semibold px-6 py-3 rounded-lg transition-colors">
                    <FileText size={18} /> Richiedi preventivo
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Catalog;
